import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiDel } from '../../../lib/api';
import { toast } from 'sonner';
import { formatDateTime } from '../../../lib/format';
import {
  Users, Search, ShieldOff, ShieldCheck, KeyRound, MailCheck, Trash2,
  RefreshCw, AlertTriangle,
} from 'lucide-react';

const STATUSES = [
  { v: '', label: 'All statuses' },
  { v: 'active', label: 'Active' },
  { v: 'approved', label: 'Approved' },
  { v: 'pending_verification', label: 'Pending verification' },
  { v: 'suspended', label: 'Suspended' },
  { v: 'deleted', label: 'Deleted' },
];

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    pending_verification: 'bg-amber-100 text-amber-800 border-amber-200',
    suspended: 'bg-red-100 text-red-800 border-red-200',
    deleted: 'bg-slate-200 text-slate-700 border-slate-300',
  };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${map[status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
      {status || 'unknown'}
    </span>
  );
};

const ConfirmDialog = ({ open, title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onClose }) => {
  if (!open) return null;
  return (
    <div data-testid="confirm-dialog" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm border border-slate-200 max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          {danger && <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" size={22} />}
          <div className="flex-1">
            <div className="font-display font-bold text-lg text-[#3b4872]">{title}</div>
            <div className="text-sm text-slate-600 mt-2">{message}</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button data-testid="confirm-cancel" onClick={onClose}
            className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-sm hover:bg-slate-50">
            Cancel
          </button>
          <button data-testid="confirm-ok" onClick={onConfirm}
            className={`px-4 py-2 text-sm font-bold rounded-sm text-white ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-[#3b4872] hover:bg-[#2d3858]'}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function VendorAccountsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q.trim()) params.q = q.trim();
      const data = await apiGet('/vendor-portal-admin/accounts', params);
      setRows(data.items || []);
    } catch (e) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const doAction = async () => {
    if (!dialog) return;
    const { action, row } = dialog;
    try {
      if (action === 'suspend') {
        await apiPost(`/vendor-portal-admin/accounts/${row.id}/suspend`, { reason });
        toast.success('Vendor suspended');
      } else if (action === 'reactivate') {
        await apiPost(`/vendor-portal-admin/accounts/${row.id}/reactivate`);
        toast.success('Vendor reactivated');
      } else if (action === 'reset_password') {
        await apiPost(`/vendor-portal-admin/accounts/${row.id}/reset-password`);
        toast.success('Reset email sent');
      } else if (action === 'force_verify') {
        await apiPost(`/vendor-portal-admin/accounts/${row.id}/force-verify-email`);
        toast.success('Email force-verified');
      } else if (action === 'delete') {
        await apiDel(`/vendor-portal-admin/accounts/${row.id}?confirm=true`);
        toast.success('Account soft-deleted');
      }
      setDialog(null);
      setReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  const dialogConfig = dialog ? {
    suspend: {
      title: `Suspend ${dialog.row.company_name || dialog.row.email}?`,
      message: (
        <div>
          <p>Suspended vendors cannot log in or place bids. Provide an optional reason for the audit log:</p>
          <textarea data-testid="suspend-reason" value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. PQ documents expired, compliance review pending"
            className="w-full mt-2 border border-slate-300 rounded-sm p-2 text-sm" rows={3} />
        </div>
      ),
      confirmLabel: 'Suspend Account',
      danger: true,
    },
    reactivate: {
      title: `Reactivate ${dialog.row.company_name || dialog.row.email}?`,
      message: 'This vendor will be able to log in again. Continue?',
      confirmLabel: 'Reactivate',
    },
    reset_password: {
      title: `Send password reset to ${dialog.row.email}?`,
      message: 'The vendor will receive a password reset link via email. You will not see or set the new password.',
      confirmLabel: 'Send Reset Email',
    },
    force_verify: {
      title: `Force-verify ${dialog.row.email}?`,
      message: 'This marks the email as verified WITHOUT the vendor clicking the link. Only use after confirming identity out-of-band.',
      confirmLabel: 'Force Verify',
      danger: true,
    },
    delete: {
      title: `Delete ${dialog.row.company_name || dialog.row.email}?`,
      message: 'This permanently disables the account (soft-delete — history preserved). They will not be able to log in. This cannot be undone from the UI.',
      confirmLabel: 'Delete Account',
      danger: true,
    },
  }[dialog.action] : null;

  return (
    <div data-testid="vendor-accounts-admin" className="space-y-4">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Vendor Portal Admin</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Users size={26} /> Vendor Accounts
        </h1>
        <p className="text-sm text-slate-600 mt-1">Suspend, reactivate, reset password, force-verify, or soft-delete vendor portal accounts.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="search-input" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Search by company, email or contact name…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </div>
        <select data-testid="status-filter" value={status} onChange={e => setStatus(e.target.value)}
          className="border border-slate-300 rounded-sm text-sm py-2 px-2">
          {STATUSES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <button data-testid="refresh-btn" onClick={load}
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="rounded-sm border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Last login</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">No accounts match the filter.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-medium">{r.company_name || '—'}</td>
                <td className="p-3">{r.name || '—'}</td>
                <td className="p-3 text-slate-600">{r.email}</td>
                <td className="p-3"><StatusBadge status={r.status} /></td>
                <td className="p-3 text-xs text-slate-500">{r.last_login ? formatDateTime(r.last_login) : '—'}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1 flex-wrap">
                    {r.status !== 'suspended' && r.status !== 'deleted' && (
                      <button data-testid={`suspend-${r.id}`} onClick={() => setDialog({ action: 'suspend', row: r })}
                        title="Suspend"
                        className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm border border-red-200">
                        <ShieldOff size={14} />
                      </button>
                    )}
                    {r.status === 'suspended' && (
                      <button data-testid={`reactivate-${r.id}`} onClick={() => setDialog({ action: 'reactivate', row: r })}
                        title="Reactivate"
                        className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded-sm border border-emerald-200">
                        <ShieldCheck size={14} />
                      </button>
                    )}
                    {r.status !== 'deleted' && (
                      <button data-testid={`reset-pw-${r.id}`} onClick={() => setDialog({ action: 'reset_password', row: r })}
                        title="Send password-reset email"
                        className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-sm border border-blue-200">
                        <KeyRound size={14} />
                      </button>
                    )}
                    {!r.email_verified && r.status !== 'deleted' && (
                      <button data-testid={`force-verify-${r.id}`} onClick={() => setDialog({ action: 'force_verify', row: r })}
                        title="Force-verify email"
                        className="p-1.5 hover:bg-amber-50 text-amber-600 rounded-sm border border-amber-200">
                        <MailCheck size={14} />
                      </button>
                    )}
                    {r.status !== 'deleted' && (
                      <button data-testid={`delete-${r.id}`} onClick={() => setDialog({ action: 'delete', row: r })}
                        title="Soft-delete account"
                        className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm border border-red-200">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog open={!!dialog} {...(dialogConfig || {})}
        onConfirm={doAction} onClose={() => { setDialog(null); setReason(''); }} />
    </div>
  );
}
